import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import Breakpoints from 'lib/Breakpoints';
import { RouteContext, VerticalContext } from 'lib/ContextTypes';
import {
  content as ContentPropType,
  coverSpreadTease as TeasePropTypes,
  responsiveFlavors as ResponsiveFlavorsPropTypes,
} from 'lib/CustomPropTypes';
import { getVerticalName } from 'lib/verticalType';

import { getPlaymakerMetadata } from 'lib/playmaker';
import Link from 'components/Link';
import MediaLabel from 'components/MediaLabel';
import TeasePicture from 'components/TeasePicture';
import Unibrow from 'components/Unibrow';
import { PlaymakerTease } from 'components/PlaymakerTease';

import { isTeasePlaymakerOverride } from '../utility';

const block = 'cover-spread-tease';

const isContentTypeWithIcons = (contentType) => (
  contentType && ['slideshow', 'video'].includes(contentType)
);

function Tease(props) {
  const vertical = React.useContext(VerticalContext);
  const route = React.useContext(RouteContext);

  const {
    content,
    headlineSizes: fallbackHeadlineSizes,
    index,
    isRailAdjacent,
    layout = {},
    layoutType,
    packageMetadata,
    responsiveFlavors: fallbackSizes,
    showInlineIcon,
  } = props;

  const {
    showEyebrow = true,
  } = packageMetadata;

  const isCover = route.path !== '/';

  const isPlaymakerTease = isTeasePlaymakerOverride(index, layoutType, packageMetadata);
  const { playmakerRenderMobileCover } = getPlaymakerMetadata(packageMetadata, vertical);

  // Set playmaker display state
  const [showPlaymaker, setShowPlaymaker] = React.useState(false);

  const breakpointListener = () => {
    if (!isPlaymakerTease) return;
    // Playmaker is enabled but configured to not render on the cover
    if (isCover && playmakerRenderMobileCover === false) {
      setShowPlaymaker(!Breakpoints.isS());
    // Otherwise show playmaker if hidden
    } else if (!showPlaymaker) {
      setShowPlaymaker(true);
    }
  };

  React.useEffect(() => {
    Breakpoints.getSmallMQL().addListener(breakpointListener);
    // Call handler right away so state gets updated with initial window size
    breakpointListener();
    // Remove event listener on cleanup
    return () => Breakpoints.getSmallMQL().removeListener(breakpointListener);
  }, []); // Empty array ensures that effect is only run on mount

  const {
    computedValues: {
      headline: computedHeadline,
      unibrow,
      url,
    } = {},
    id,
    item = {},
    type,
  } = content;

  const {
    grid,
    headlineSizes,
    headlineTextPositioning = '',
    image: {
      display = '',
      sizes,
    } = {},
    mediaLabel: {
      eyebrowDisplay = '',
      iconBlockDisplay = '',
      iconInlineDisplay = '',
    } = {},
    unibrowDisplay = '',
  } = get(layout, `tease${index}`, {});

  const verticalName = getVerticalName({ item });

  const headline = computedHeadline || get(item, ['headline', 'primary'], null);

  // Editors set custom flag text (whether live or not), show that text
  const customFlag = get(content, ['metadata', 'flag'], null);

  return (
    <div className={classNames(block, grid)} data-contentid={id}>
      <div className={index >= 0 ? `${block}--${index}` : ''}>
        {showPlaymaker ? (
          <PlaymakerTease
            icid="tease-coverspread"
            metadata={packageMetadata}
            showDek={false}
            stickyEnabled
            styleOverride={{
              headline: headlineSizes,
            }}
          />
        ) : (
          <>
            <div
              className={classNames(
                `${block}__image`,
                display,
                { [`${block}__image--rail`]: isRailAdjacent },
              )}
              data-test={`${block}__image`}
            >
              <Link href={url} className={`${block}__image-link`}>
                <TeasePicture
                  className={`${block}__picture`}
                  computedValues={content.computedValues}
                  lazyload
                  placeholder={false}
                  responsiveFlavors={sizes || fallbackSizes}
                  retina
                  type={type}
                />
              </Link>
              <MediaLabel
                content={content}
                eyebrowClasses={eyebrowDisplay}
                iconBlockClasses={iconBlockDisplay}
              />
            </div>
            <div
              className={`${block}__text-wrapper`}
              data-test={`${block}__text-wrapper`}
            >
              {(showEyebrow && unibrow.text) && (
                <Unibrow
                  unibrow={unibrow}
                  className={classNames(
                    `${block}__unibrow`,
                    unibrowDisplay,
                    {
                      [`${block}__unibrow--opinion`]: verticalName === 'think',
                    },
                  )}
                />
              )}
              <h3
                className={classNames(
                  `${block}__headline`,
                  headlineSizes || fallbackHeadlineSizes,
                  { [`${block}__headline--rail`]: isRailAdjacent },
                )}
                data-test={`${block}__headline`}
              >
                <Link
                  className={`${block}__headline-link`}
                  to={url}
                >
                  {showInlineIcon && (
                    <MediaLabel
                      content={content}
                      showInlineIcon={showInlineIcon}
                      iconInlineClasses={iconInlineDisplay}
                    />
                  )}
                  {/* TODO: BENTO-15783 extract this flag to sub component */}
                  {customFlag && (
                    <span className={`${block}__headline__flag`}>
                      {customFlag}
                      &nbsp;
                      {'/ '}
                    </span>
                  )}
                  <span
                    className={classNames({
                      [headlineTextPositioning]: isContentTypeWithIcons(type),
                    })}
                    data-test={`${block}__headline__text`}
                  >
                    {headline}
                  </span>
                </Link>
              </h3>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

Tease.propTypes = {
  content: ContentPropType.isRequired,
  headlineSizes: PropTypes.string,
  index: PropTypes.number,
  isRailAdjacent: PropTypes.bool,
  layout: TeasePropTypes,
  layoutType: PropTypes.string,
  packageMetadata: PropTypes.shape({
    showEyebrow: PropTypes.bool,
  }),
  responsiveFlavors: ResponsiveFlavorsPropTypes,
  showInlineIcon: PropTypes.bool,
};

Tease.defaultProps = {
  headlineSizes: '',
  index: null,
  isRailAdjacent: false,
  layout: {},
  layoutType: 'default',
  packageMetadata: {
    showEyebrow: false,
  },
  responsiveFlavors: {},
  showInlineIcon: false,
};

export { Tease };
